import path from 'path';

import { createCanvas, registerFont } from 'canvas';
import isNode from 'detect-node';

import type { AlineFontName } from '../slate-api';

type NodePath = string;
interface CommonFontItem {
  bold: NodePath;
  boldItalic: NodePath;
  italic: NodePath;
  regular: NodePath;
}
type CommonFontMap = Record<AlineFontName, CommonFontItem>;
const fontDirPath = '../../../fonts/src/fonts';

const commonFontMap: Partial<CommonFontMap> = {
  Arial: {
    bold: `${fontDirPath}/arial/ArialBold.ttf`,
    boldItalic: `${fontDirPath}/arial/ArialBoldItalic.ttf`,
    italic: `${fontDirPath}/arial/ArialItalic.ttf`,
    regular: `${fontDirPath}/arial/Arial.ttf`,
  },
  Calibri: {
    bold: `${fontDirPath}/calibri/CalibriBold.ttf`,
    boldItalic: `${fontDirPath}/calibri/CalibriBoldItalic.ttf`,
    italic: `${fontDirPath}/calibri/CalibriItalic.ttf`,
    regular: `${fontDirPath}/calibri/Calibri.ttf`,
  },
  Cambria: {
    bold: `${fontDirPath}/cambria/CambriaBold.ttf`,
    boldItalic: `${fontDirPath}/cambria/CambriaBoldItalic.ttf`,
    italic: `${fontDirPath}/cambria/CambriaItalic.ttf`,
    regular: `${fontDirPath}/cambria/Cambria.ttf`,
  },
  'Century Gothic': {
    bold: `${fontDirPath}/century-gothic/CenturyGothicBold.ttf`,
    boldItalic: `${fontDirPath}/century-gothic/CenturyGothicBoldItalic.ttf`,
    italic: `${fontDirPath}/century-gothic/CenturyGothicItalic.ttf`,
    regular: `${fontDirPath}/century-gothic/CenturyGothic.ttf`,
  },
  'Courier New': {
    bold: `${fontDirPath}/courier-new/CourierNewBold.ttf`,
    boldItalic: `${fontDirPath}/courier-new/CourierNewBoldItalic.ttf`,
    italic: `${fontDirPath}/courier-new/CourierNewItalic.ttf`,
    regular: `${fontDirPath}/courier-new/CourierNewRegular.ttf`,
  },
  /* Note: Franklin Gothic Medium does not have bold so we re-use Regular and Italic on purpose. */
  'Franklin Gothic Medium': {
    bold: `${fontDirPath}/franklin-gothic/FranklinGothicMedium.ttf`,
    boldItalic: `${fontDirPath}/franklin-gothic/FranklinGothicMediumItalic.ttf`,
    italic: `${fontDirPath}/franklin-gothic/FranklinGothicMediumItalic.ttf`,
    regular: `${fontDirPath}/franklin-gothic/FranklinGothicMedium.ttf`,
  },
  'Free Serif': {
    bold: `${fontDirPath}/free-serif/FreeSerif.ttf`,
    boldItalic: `${fontDirPath}/free-serif/FreeSerif.ttf`,
    italic: `${fontDirPath}/free-serif/FreeSerif.ttf`,
    regular: `${fontDirPath}/free-serif/FreeSerif.ttf`,
  },
  Garamond: {
    bold: `${fontDirPath}/garamond/GaramondBold.ttf`,
    boldItalic: `${fontDirPath}/garamond/GaramondBoldItalic.ttf`,
    italic: `${fontDirPath}/garamond/GaramondItalic.ttf`,
    regular: `${fontDirPath}/garamond/GaramondRegular.ttf`,
  },
  Georgia: {
    bold: `${fontDirPath}/georgia/GeorgiaBold.ttf`,
    boldItalic: `${fontDirPath}/georgia/GeorgiaBoldItalic.ttf`,
    italic: `${fontDirPath}/georgia/GeorgiaItalic.ttf`,
    regular: `${fontDirPath}/georgia/GeorgiaRegular.ttf`,
  },
  Helvetica: {
    bold: `${fontDirPath}/helvetica/HelveticaBold.ttf`,
    boldItalic: `${fontDirPath}/helvetica/HelveticaBoldItalic.ttf`,
    italic: `${fontDirPath}/helvetica/HelveticaItalic.ttf`,
    regular: `${fontDirPath}/helvetica/HelveticaRegular.ttf`,
  },
  'Helvetica Neue': {
    bold: `${fontDirPath}/helvetica-neue/HelveticaNeueBold.ttf`,
    boldItalic: `${fontDirPath}/helvetica-neue/HelveticaNeueBoldItalic.ttf`,
    italic: `${fontDirPath}/helvetica-neue/HelveticaNeueItalic.ttf`,
    regular: `${fontDirPath}/helvetica-neue/HelveticaNeueRegular.ttf`,
  },
  'Lucida Sans Typewriter': {
    bold: `${fontDirPath}/lucida-sans/LucidaSansTypewriterBold.ttf`,
    boldItalic: `${fontDirPath}/lucida-sans/LucidaSansTypewriterBoldItalic.ttf`,
    italic: `${fontDirPath}/lucida-sans/LucidaSansTypewriterItalic.ttf`,
    regular: `${fontDirPath}/lucida-sans/LucidaSansTypewriterRegular.ttf`,
  },
  'Open Sans': {
    bold: `${fontDirPath}/open-sans/OpenSansBold.ttf`,
    boldItalic: `${fontDirPath}/open-sans/OpenSansBoldItalic.ttf`,
    italic: `${fontDirPath}/open-sans/OpenSansItalic.ttf`,
    regular: `${fontDirPath}/open-sans/OpenSansRegular.ttf`,
  },
  Palatino: {
    bold: `${fontDirPath}/palatino/PalatinoBold.ttf`,
    boldItalic: `${fontDirPath}/palatino/PalatinoBoldItalic.ttf`,
    italic: `${fontDirPath}/palatino/PalatinoItalic.ttf`,
    regular: `${fontDirPath}/palatino/PalatinoRegular.ttf`,
  },
  Symbol: {
    bold: `${fontDirPath}/symbol/Symbol.ttf`,
    boldItalic: `${fontDirPath}/symbol/Symbol.ttf`,
    italic: `${fontDirPath}/symbol/Symbol.ttf`,
    regular: `${fontDirPath}/symbol/Symbol.ttf`,
  },
  Tahoma: {
    bold: `${fontDirPath}/tahoma/TahomaBold.ttf`,
    boldItalic: `${fontDirPath}/tahoma/TahomaBoldItalic.ttf`,
    italic: `${fontDirPath}/tahoma/TahomaItalic.ttf`,
    regular: `${fontDirPath}/tahoma/Tahoma.ttf`,
  },
  'Times New Roman': {
    bold: `${fontDirPath}/times-new-roman/TimesNewRomanBold.ttf`,
    boldItalic: `${fontDirPath}/times-new-roman/TimesNewRomanBoldItalic.ttf`,
    italic: `${fontDirPath}/times-new-roman/TimesNewRomanItalic.ttf`,
    regular: `${fontDirPath}/times-new-roman/TimesNewRoman.ttf`,
  },
  Verdana: {
    bold: `${fontDirPath}/verdana/VerdanaBold.ttf`,
    boldItalic: `${fontDirPath}/verdana/VerdanaBoldItalic.ttf`,
    italic: `${fontDirPath}/verdana/VerdanaItalic.ttf`,
    regular: `${fontDirPath}/verdana/Verdana.ttf`,
  },
};

/**
 * Creates a canvas using node-canvas and loads any necessary fonts before returning the canvas, if needed.
 */
export function createAlineCanvas(
  width = 250,
  height = 250,
  type?: 'pdf' | 'svg'
): ReturnType<typeof createCanvas> {
  // Important to registerFonts in a Node env *before* creating canvas.  See https://www.npmjs.com/package/canvas#registerfont.
  if (isNode) {
    Object.entries(commonFontMap).forEach(([fontName, { bold, boldItalic, italic, regular }]) => {
      registerFont(path.resolve(__dirname, bold), {
        family: fontName,
        style: 'normal',
        weight: 'bold',
      });
      registerFont(path.resolve(__dirname, boldItalic), {
        family: fontName,
        style: 'italic',
        weight: 'bold',
      });
      registerFont(path.resolve(__dirname, italic), {
        family: fontName,
        style: 'italic',
        weight: 'normal',
      });
      registerFont(path.resolve(__dirname, regular), {
        family: fontName,
        style: 'normal',
        weight: 'normal',
      });
    });
  }

  return createCanvas(width, height, type);
}
